import React, {
  useState, useEffect, forwardRef, useImperativeHandle, useRef,
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import DynamicForm, { ChangedField } from 'shared/form';
import { MainFormData } from 'data/distributorConfiguration/distributorConfiguration.types';
import { Distributor, DistributorData } from 'data/distributor/distributor.types';
import { NodeData } from 'data/nodes/nodes.types';
import { FormTemplateOption, FormTemplateSection } from 'data/template/template.types';
import { setFieldOptions } from 'data/distributorConfiguration/distributorConfiguration.adapter';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';

export type Props = {
  formData: MainFormData,
  templateSection: FormTemplateSection,
  distributors: DistributorData,
  nodes: NodeData[],
  availableAccessFunction: SupportedFeaturesAccessFunctions
  enabledAccessFunction: SupportedFeaturesAccessFunctions
  i18nKeyPrefix: string
  fieldUpdateHandler: (field: ChangedField) => void
}
export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => MainFormData
}

const MainForm = forwardRef<Ref, Props>(({
  formData,
  templateSection,
  distributors,
  nodes,
  availableAccessFunction,
  enabledAccessFunction,
  i18nKeyPrefix,
  fieldUpdateHandler,
}: Props, ref) => {
  const formRef = useRef<DynamicForm>(null);

  const addOptionIfMissing = (
    options: FormTemplateOption[],
    value?: string,
    label?: string,
  ) => {
    if (!value) {
      return;
    }

    const isAlreadyIncluded = options.some((option) => option.value === value);
    if (!isAlreadyIncluded) {
      options.push({
        label: label ?? value,
        value,
      });
    }
  };

  const setModuleOptions = (newTemplate: FormTemplateSection) => {
    const moduleNameSet: Set<string> = new Set<string>();

    distributors?.ss8Distributors.forEach(({ moduleName, afTags }) => {
      const shouldModuleNameBeAvailable = afTags.some((afTag) => !!enabledAccessFunction[afTag]);
      if (shouldModuleNameBeAvailable) {
        moduleNameSet.add(moduleName);
      }
    });

    const options = Array.from(moduleNameSet).map((moduleName) => {
      return {
        label: moduleName,
        value: moduleName,
      };
    });

    addOptionIfMissing(options, formData?.moduleName);
    options.sort((a, b) => a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()));

    setFieldOptions(newTemplate, options, 'moduleName');
  };

  const setInstanceIdOptions = (newTemplate: FormTemplateSection) => {
    const options: FormTemplateOption[] = [];
    distributors?.ss8Distributors.forEach((distributor:Distributor) => {
      if (formData?.moduleName === distributor.moduleName) {
        const { instanceId } = distributor;
        options.push({
          label: `${instanceId}`,
          value: `${instanceId}`,
        });
      }
    });

    addOptionIfMissing(options, formData?.instanceId);
    setFieldOptions(newTemplate, options, 'instanceId');
  };

  const setAfTypeOptions = (newTemplate: FormTemplateSection) => {
    const dist = distributors?.ss8Distributors.find((distributor: Distributor) => (
      formData?.moduleName === distributor.moduleName
      && formData?.instanceId === distributor.instanceId.toString()
    ));
    const options = dist?.afTags.map((afTag) => {
      return ({
        label: availableAccessFunction[afTag]?.title ?? afTag,
        value: afTag,
      });
    }) ?? [];

    const afTypeLabel = formData?.afType ? availableAccessFunction[formData.afType]?.title : undefined;
    addOptionIfMissing(options, formData?.afType, afTypeLabel);
    setFieldOptions(newTemplate, options, 'afType');
  };

  const setNodeNameOptions = (newTemplate: FormTemplateSection) => {
    if (!formData?.moduleName) {
      setFieldOptions(newTemplate, [], 'nodeName');
      return;
    }

    const options: {label:string, value:string}[] = [];
    nodes?.forEach((node: NodeData) => {
      options.push({
        label: `${node.nodeName} (${node.nodeType})`,
        value: node.nodeName,
      });
    });

    addOptionIfMissing(options, formData?.nodeName);
    setFieldOptions(newTemplate, options, 'nodeName');
  };

  const [template, setTemplate] = useState(cloneDeep(templateSection));

  useEffect(() => {
    const newTemplate = cloneDeep(templateSection);
    setModuleOptions(newTemplate);
    setInstanceIdOptions(newTemplate);
    setAfTypeOptions(newTemplate);
    setNodeNameOptions(newTemplate);
    setTemplate(newTemplate);
  }, [
    templateSection,
    distributors,
    nodes,
    availableAccessFunction,
    enabledAccessFunction,
    formData?.moduleName,
    formData?.instanceId,
    formData?.afType,
    formData?.nodeName,
  ]);

  const isValid = (forceValidation = false, forceTouched = true) => {
    return formRef.current?.isValid(forceValidation, forceTouched) ?? true;
  };

  const getValues = (): MainFormData => formRef.current?.getValues();

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  return (
    <div>
      <DynamicForm
        ref={formRef}
        data-test="ListenerDetail-form"
        name={`listener_${new Date().getTime()}`}
        fields={template.fields}
        layout={template.metadata?.layout?.rows}
        defaults={formData}
        i18nKeyPrefix={i18nKeyPrefix}
        fieldUpdateHandler={fieldUpdateHandler}
      />
    </div>
  );
});

export default MainForm;
